/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Card,
  Tabs,
  message,
} from 'antd';
import {
  InfoCircleOutlined,
} from '@ant-design/icons';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { onError } from 'utils/handlers';
import { ActionsNames, hasActionPermission } from 'utils/permission/index';
import { getUsuario, getUsuarios } from 'api/usuarios/usuarios';
import {
  deleteDetalleDeUsuario,
  getDetalleDeUsuario,
  getDetallesDeUsuarios,
  patchDetalleDeUsuario,
  postDetalleDeUsuario,
} from 'api/recaudacion/detallesDeUsuario';
import { getTiposDeInspectores } from 'api/recaudacion/tipoDeInspectores';
import { InputSearchPro } from 'components/InputSearch';
import BusquedaAvanzadaUsers from 'components/BusquedaAvanzadaCajeros';

const { TabPane } = Tabs;

const DetalleDeUsuarios = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [userFound, setUserFound] = useState([]);
  const [tipoDeInspector, setTipoDeInspector] = useState([]);
  const [visibleBusqueda, setVisibleBusqueda] = useState(false);

  const models = ['tipodeinspector'];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getDetallesDeUsuarios();
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    let _hasAllPermission = true;
    const validatePermission = () => {
      const _hasAllViewPermission = models
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        _hasAllPermission = _hasAllViewPermission;
        setHasAllViewPermission(_hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const [_data, _tipoInspector] = await Promise.all([
        getDetallesDeUsuarios(),
        getTiposDeInspectores(),
      ]);
      if (mounted) {
        setData(_data);
        setTipoDeInspector(_tipoInspector);
        setLoading(false);
      }
    };
    validatePermission();
    if (_hasAllPermission) {
      fetchAll();
    }
    return () => { mounted = false; };
  }, []);

  const reset = () => {
    form.resetFields(['q']);
    form.validateFields(['q']);
  };

  const fetchUser = async (id) => {
    setLoading(true);
    if (id) {
      const _user = await getUsuario(id?.id || id);
      if (_user) {
        form.setFieldsValue({
          usuario: _user.id,
        });
        const matched = {
          ..._user,
          nombre_completo: `${_user?.first_name} ${_user?.last_name
            || ''} ${_user?.second_last_name || ''}`,
        };
        setUserFound(matched);
      } else {
        message.info('No se encontró usuario');
      }
    }
    setLoading(false);
  };

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setUserFound([]);
  };

  const onClickEdit = async (id) => {
    const key = selectedRowKeys[0] || id;
    const match = await getDetalleDeUsuario(key);
    if (match) {
      await fetchUser(match.usuario);
      form.setFieldsValue(match);
    }
    setVisible(true);
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const _data = await (key ? patchDetalleDeUsuario(key, values)
        : postDetalleDeUsuario(values)
      );
      if (_data) {
        await fetchData();
        if (_continue) {
          if (!key) {
            setSelectedRowKeys([_data.id]);
          }
          await onClickEdit(_data.id);
        } else {
          onCancel();
        }
        setLoading(false);
        return _data;
      }
      setLoading(false);
      return true;
    } catch (err) {
      onError(err, setLoading);
      return false;
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteDetalleDeUsuario(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave_inspector',
      key: 'clave_inspector',
      width: 200,
    },
    {
      titleText: 'Tipo de Inspector',
      dataIndex: 'tipo_de_inspector',
      key: 'tipo_de_inspector',
      width: 200,
      catalog: {
        cat: tipoDeInspector,
      },
    },
  ];

  const rules = {
    required: [
      { required: true },
    ],
    email: [
      {
        type: 'email',
      },
    ],
  };
  const searchUsuario = async () => {
    try {
      setLoading(true);
      const { q: email } = form.getFieldsValue();
      const _usuario = await getUsuarios({ email });

      if (_usuario.length) {
        form.setFieldsValue({
          usuario: _usuario[0].id,
        });
        const _cajero = _usuario.map((e) => ({
          ...e,
          nombre_completo: `${e.first_name} ${e.last_name || ''} ${e.second_last_name || ''}`,
        }));
        setUserFound([_cajero[0]]);
      } else {
        message.info('No se encontraron coincidencias');
        setUserFound([]);
      }
      setLoading(false);
    } catch (error) {
      onError(error, setLoading);
    }
  };
  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible
          ? (
            <Table
              cols={columns}
              mobileColumns={[columns[0], columns[1]]}
              data={data}
              rowSelection={rowSelection}
              handleOnRowClick={handleOnRowClick}
              controls={{
                onClickAdd,
                onClickEdit,
                onClickDelete,
              }}
              rowKey="id"
              permission={permission}
              hasAllViewPermission={hasAllViewPermission}
            />
          ) : (
            <Col span={24} style={{ height: '100%' }}>
              <Card
                className="form-container"
                title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Detalle de Usuarios Inpsectores`}
                extra={(
                  <FormSubmitControls
                    onFinish={onFinish}
                    onCancel={onCancel}
                    allowSaveAndContinue
                    onCancelText="Cancelar"
                    permission={permission}
                  />
                )}
                bordered={false}
              >
                <Tabs
                  type="card"
                >
                  <TabPane
                    tab={(
                      <span>
                        <InfoCircleOutlined />
                        Información General
                      </span>
                    )}
                    key="general"
                    forceRender
                  >
                    <Form
                      name="general"
                      layout="vertical"
                      form={form}
                      onFinish={() => onFinish()}
                      scrollToFirstError
                    >
                      <Row gutter={10}>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="clave_inspector"
                            label="Clave del Inspector"
                            hasFeedback
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="tipo_de_inspector"
                            label="Tipo de Inspector"
                            hasFeedback
                          >
                            <Select dataSource={tipoDeInspector} />
                          </Form.Item>
                        </Col>
                        <InputSearchPro
                          label="Usuario"
                          tooltip="Búsqueda por Correo del Usuario"
                          name="q"
                          rules={rules.email}
                          onClickAdvanced={() => setVisibleBusqueda(true)}
                          onPressEnter={() => searchUsuario()}
                          breakPoints={{
                            md: 8,
                          }}
                          resultProps={{
                            name: 'usuario',
                            label: 'Usuario',
                            dataSource: userFound,
                            labelProp: 'nombre_completo',
                            onClear: () => reset(),
                            breakPoints: {
                              md: 8,
                            },
                          }}
                        />
                      </Row>
                      <Form.Item hidden>
                        <Button htmlType="submit" />
                      </Form.Item>
                    </Form>
                  </TabPane>
                </Tabs>
              </Card>
            </Col>
          )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          visible={visibleAlert}
          content="Detalle Seleccionado"
          loading={loading}
        />
        <BusquedaAvanzadaUsers
          visible={visibleBusqueda}
          setVisible={setVisibleBusqueda}
          callback={fetchUser}
          dataExterno={[]}
          setDataExterno={() => { }}
        />
      </Spin>
    </Row>
  );
};

export default DetalleDeUsuarios;
